import { useEffect, useMemo } from "react";
import { createFileRoute, redirect, Outlet, useRouterState } from "@tanstack/react-router";
import { cn, OverlayLockProvider } from "@nexus/ui";
import { getExpiresAt } from "@/features/auth/lib/token";
import { refreshNexusToken } from "@/features/auth/lib/refresh";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { AppShell } from "@/components/layout/AppShell";
import { AppUnavailable } from "@/features/auth/components/AppUnavailable";
import { ConnectionLostBanner } from "@/components/ConnectionLostBanner";
import { VersionUpdateBanner } from "@/components/VersionUpdateBanner";
import { TaskProgressSidebar } from "@/features/task-progress/components/TaskProgressSidebar";
import { ExploreFilterHeader } from "@/features/dashboard/components/layout/ExploreFilterHeader";
import { EXPLORE_ROUTES } from "@/features/dashboard/lib/explore-search";
import { routeToPageSlug } from "@/features/auth/lib/page-routes";
import { useServerStatusStore } from "@/hooks/serverStatusStore";
import { usePersistRouteSearchOnLeave } from "@/hooks/usePersistRouteSearchOnLeave";
import { creativeUrlSettingsQueryOptions } from "@/lib/queries/system-settings.queries";

export const Route = createFileRoute("/_authenticated")({
  loader: ({ context }) => context.queryClient.ensureQueryData(creativeUrlSettingsQueryOptions()),
  beforeLoad: async () => {
    const expiresAt = getExpiresAt();
    const nowSeconds = Math.floor(Date.now() / 1000);

    if (!expiresAt || expiresAt <= nowSeconds) {
      try {
        await refreshNexusToken();
        return;
      } catch {
        throw redirect({ to: "/login" });
      }
    }
  },
  component: AuthenticatedLayout,
  errorComponent: AppUnavailable,
});

/** Strong enough that charts/pills clearly look unavailable; content stays readable. */
const OFFLINE_SHELL_FILTER =
  "pointer-events-none select-none opacity-50 saturate-[0.2] brightness-[0.96]";

function AuthenticatedLayout() {
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const setLastVisitedRoute = useAuth((s) => s.setLastVisitedRoute);
  const isServerOffline = useServerStatusStore((s) => s.status === "offline");
  const { pathname, href } = useRouterState({
    select: (s) => ({ pathname: s.location.pathname, href: s.location.href }),
  });

  const hasExploreAccess = useMemo(() => {
    if (!EXPLORE_ROUTES.has(pathname)) return false;
    const slug = routeToPageSlug(pathname);
    if (!slug) return false;
    const activeTenant = tenants.find((t) => t.id === activeTenantId);
    return activeTenant?.permissions.some((p) => p.type === "PAGE" && p.route === slug) ?? false;
  }, [pathname, tenants, activeTenantId]);

  usePersistRouteSearchOnLeave(pathname, href);

  // Track the last visited route for re-login redirect.
  // Keyed only on pathname — never re-runs from search-param changes.
  useEffect(() => {
    if (pathname && pathname !== "/") {
      setLastVisitedRoute(pathname);
    }
  }, [pathname, setLastVisitedRoute]);

  return (
    // Dialog/Sheet portal to document.body, outside the inert wrapper below.
    // The lock provider reaches them through React context so the modal layer
    // blocks together with the shell while offline.
    <OverlayLockProvider locked={isServerOffline}>
      <ConnectionLostBanner />
      <VersionUpdateBanner />
      <TaskProgressSidebar />
      <div
        inert={isServerOffline || undefined}
        aria-busy={isServerOffline || undefined}
        className={cn(
          "transition-[opacity,filter] duration-300 ease-out motion-reduce:transition-none",
          isServerOffline && OFFLINE_SHELL_FILTER,
        )}
      >
        <AppShell tenants={tenants}>
          {hasExploreAccess && <ExploreFilterHeader />}
          <Outlet />
        </AppShell>
      </div>
    </OverlayLockProvider>
  );
}
